/**
 * orders.ts — domain zod schemas for the orders store.
 *
 * Row schemas are independent of db-types.ts (which is auto-generated by
 * `pnpm gen:types` after migration — do NOT edit db-types.ts manually).
 *
 * NUMERIC columns (total, price) arrive over PostgREST as JSON strings — typed
 * `z.string()` here (decimal-string), never `z.number()`, to preserve money
 * precision (decimal.js). JSONB columns are typed as nested schemas.
 *
 * Source: 2026-06-25-wandero-local-orders-copy-store spec §D2 / LOCKED schema.
 */

import { z } from 'zod';
import { OwnPhotoOriginalRef } from './own-photo.js';

// ── Status enum ──────────────────────────────────────────────────────────────

export const ORDER_STATUSES = [
  'pending',
  'paid',
  'accepted',
  'done',
] as const;

/** Order lifecycle status. Maps 1-to-1 with the `order_status` Postgres enum. */
export const OrderStatus = z.enum(ORDER_STATUSES);
export type OrderStatus = z.infer<typeof OrderStatus>;

// ── OrderType ────────────────────────────────────────────────────────────────

/**
 * Order-level discriminator, mirrors the `orders.type` TEXT+CHECK column
 * (`20260718150649_orders_type.sql` — TEXT+CHECK, not a Postgres enum, for
 * cheap future extension; app-level type-safety lives here in zod). Derived
 * server-side in `doInsertOrder` from `items.some(i => i.source ===
 * 'own_photo')` — never trusted verbatim from the client (see
 * `CreateOrderRequestSchema.type` below, an optional override only).
 */
export const ORDER_TYPES = ['catalog', 'own_photo'] as const;
export const OrderType = z.enum(ORDER_TYPES);
export type OrderType = z.infer<typeof OrderType>;

// ── OrderItemSnapshot ────────────────────────────────────────────────────────

/**
 * Immutable snapshot of a single cart line at the moment of order creation.
 * Stored as JSONB in `orders.items` — never recalculated after order creation.
 *
 * Money fields (price) are decimal-strings for precision (decimal.js).
 * Integer fields (ids, dimensions, roll_count) are z.number().int().
 * Fractional physical measures (area_m2) are decimal-strings for precision.
 *
 * Additive `source` discriminator (2026-07-18-custom-photo-order-page):
 * `'catalog'` (default) requires `wallpaper_id`, forbids `original`;
 * `'own_photo'` requires `original` (uploaded-image ref), forbids
 * `wallpaper_id`. Mirrors the `CartItem` XOR in `cart.ts` — the order
 * snapshot is written straight from cart lines at checkout.
 */
export const OrderItemSnapshot = z
  .object({
    source: z.enum(['catalog', 'own_photo']).default('catalog'),
    // Required when source==='catalog'; enforced by superRefine, not the base
    // shape, so this stays additive/back-compatible with existing snapshots.
    wallpaper_id: z.number().int().positive().optional(),
    // Required when source==='own_photo'.
    original: OwnPhotoOriginalRef.optional(),
    material_id: z.number().int().positive(),
    /** Width in centimetres (integer). */
    width_cm: z.number().int().positive(),
    /** Height in centimetres (integer). */
    height_cm: z.number().int().positive(),
    /** Number of rolls. */
    roll_count: z.number().int().positive(),
    /** Area in square metres — decimal-string for precision. */
    area_m2: z.string().max(32),
    /** Line total — decimal-string, already with stacked discounts applied. */
    price: z.string().max(32),
    /**
     * Pre-promo baked line total (mirrors `CartItem.price_total`) — decimal-
     * string. Optional: the server-side promo re-check (2026-06-25-wandero-
     * email-promo-code-discount, B4 checkout-promo fix) uses this + below as
     * its OWN recompute input via the shared `promoCartTotal` helper, so the
     * client-supplied `price`/order `total` is never the trusted figure when
     * a `promo_code` is present. Omit for orders without a promo code
     * (fully backward-compatible).
     */
    price_total: z.string().max(32).optional(),
    /**
     * Pre-promo baked+schedule/segment-discounted line total, or `null` when
     * no schedule discount was baked. Same optionality/purpose as
     * `price_total` above.
     */
    price_discounted: z.string().max(32).nullable().optional(),
  })
  .superRefine((item, ctx) => {
    if (item.source === 'catalog') {
      if (item.wallpaper_id === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['wallpaper_id'],
          message: "wallpaper_id is required when source is 'catalog'",
        });
      }
      if (item.original !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['original'],
          message: "original must be absent when source is 'catalog'",
        });
      }
    } else {
      if (item.original === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['original'],
          message: "original is required when source is 'own_photo'",
        });
      }
      if (item.wallpaper_id !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['wallpaper_id'],
          message: "wallpaper_id must be absent when source is 'own_photo'",
        });
      }
    }
  });
export type OrderItemSnapshot = z.infer<typeof OrderItemSnapshot>;

// ── OrderCustomerSnapshot ────────────────────────────────────────────────────

/**
 * PII snapshot of customer contact + delivery details at order creation.
 * Stored as JSONB in `orders.customer`.
 */
export const OrderAddressSnapshot = z.object({
  ulica: z.string().max(200),
  miasto: z.string().max(200),
  kod: z.string().max(200),
  kraj: z.string().max(200),
});
export type OrderAddressSnapshot = z.infer<typeof OrderAddressSnapshot>;

export const OrderCustomerSnapshot = z.object({
  email: z.string().email().max(254),
  phone: z.string().max(200).optional(),
  imie: z.string().max(200),
  nazwisko: z.string().max(200),
  adres: OrderAddressSnapshot,
});
export type OrderCustomerSnapshot = z.infer<typeof OrderCustomerSnapshot>;

// ── OrderRowSchema ───────────────────────────────────────────────────────────

/**
 * Full order row as returned by PostgREST (service-role).
 *
 * - `id` — internal UUID, not exposed to end users
 * - `total` — NUMERIC(12,2) arrives as decimal-string
 * - `customer` / `items` — JSONB, parsed with nested schemas
 * - `payment` — JSONB, nullable (null = not paid yet)
 * - Timestamps — ISO strings from PostgREST
 */
export const OrderRowSchema = z.object({
  id: z.string().uuid(),
  order_number: z.string(),
  email: z.string().email(),
  status: OrderStatus,
  /** `'catalog'` (default) or `'own_photo'` — derived server-side from items. */
  type: OrderType,
  /** JSONB: stub or real payment callback. Null until mark-paid. */
  payment: z.record(z.string(), z.unknown()).nullable(),
  /** A-Manager reference filled post-handoff (out of scope for initial version). */
  amanager_ref: z.string().nullable(),
  /** NUMERIC(12,2) as decimal-string. Total with stacked discounts. */
  total: z.string(),
  /** ISO-3 currency code. */
  currency: z.string(),
  /** Immutable customer PII snapshot. */
  customer: OrderCustomerSnapshot,
  /** Immutable line-items snapshot. */
  items: z.array(OrderItemSnapshot),
  consent_pii: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type OrderRow = z.infer<typeof OrderRowSchema>;
